import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../../hooks/useLocalizedPath';
import { Section, Container, Button } from "../../ui";
import styles from "./Hero.module.css";

export function Hero() {
  const { t } = useTranslation('home');
  const { lp } = useLocalizedPath();
  const audiences = t('hero.audiences', { returnObjects: true }) as string[];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [animState, setAnimState] = useState<'visible' | 'exiting'>('visible');

  const cycle = useCallback(() => {
    setAnimState('exiting');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % audiences.length);
      setAnimState('visible');
    }, 500);
  }, [audiences.length]);

  useEffect(() => {
    const interval = setInterval(cycle, 3500);
    return () => clearInterval(interval);
  }, [cycle]);

  return (
    <Section variant="dark" className={styles.hero}>
      <div className={styles.backgroundImage}>
        <video autoPlay muted loop playsInline aria-hidden="true" tabIndex={-1} preload="none" poster="/images/hero-workshop-poster.webp">
          <source src="/images/hero-workshop.mp4" type="video/mp4" />
        </video>
        <div className={styles.overlay} />
      </div>
      <div className={styles.glow} />
      <Container>
        <div className={styles.content}>
          <h1 className={styles.title}>
            <span className={styles.audienceRow}>
              <span
                className={`${styles.audienceWord} ${styles[animState]}`}
                aria-live="polite"
                aria-atomic="true"
              >
                {audiences[currentIndex]}
              </span>
            </span>
            <span className={styles.valueProp}>{t('hero.valueProp')}</span>
          </h1>
          <p className={styles.subtitle}>{t('hero.subtitle')}</p>
          <div className={styles.ctas}>
            <Button href={lp('/solutions')}>{t('hero.ctaSolutions')}</Button>
            <Button href={lp('/contact')} variant="secondary" data-attr="cta-contact">
              {t('hero.ctaContact')}
            </Button>
          </div>
        </div>
      </Container>
      <div className={styles.scrollIndicator} aria-hidden="true">
        <div className={styles.chevron} />
        <div className={styles.chevron} />
        <div className={styles.chevron} />
      </div>
    </Section>
  );
}
